import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useSeason } from './useSeason';
import type { UseWeeklyAveragesReturn, WeeklyAverage } from '../types';

export function useWeeklyAverages(
  bowlerId: number | null, 
  seasonYearOverride?: number
): UseWeeklyAveragesReturn {
  const { selectedSeason } = useSeason();
  const seasonYear = seasonYearOverride ?? selectedSeason;
  const [weeklyData, setWeeklyData] = useState<WeeklyAverage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchWeeklyAverages = useCallback(async () => {
    if (!bowlerId) {
      setWeeklyData([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('get_weekly_averages', {
          target_bowler_id: bowlerId,
          season_year: seasonYear,
        });

      if (fetchError) {
        throw new Error(`Failed to fetch weekly averages: ${fetchError.message}`);
      }

      const transformedData: WeeklyAverage[] = (data || []).map((week: Record<string, unknown>) => ({
        week_date: week.week_date as string,
        week_number: week.week_number as number,
        average_score: week.average_score as number,
        games_played: week.games_played as number,
        total_pins: week.total_pins as number,
      }));

      setWeeklyData(transformedData);
    } catch (err) {
      console.error('Error fetching weekly averages:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch weekly averages'));
    } finally {
      setLoading(false);
    }
  }, [bowlerId, seasonYear]);

  const refetch = async () => {
    await fetchWeeklyAverages();
  };

  useEffect(() => {
    fetchWeeklyAverages();
  }, [fetchWeeklyAverages]);

  return {
    weeklyData,
    loading,
    error,
    refetch,
    clearError: () => setError(null),
    retryLastOperation: refetch,
  };
}